import React, { useState, useEffect } from "react";

import useDataSourceManager from "hooks/useDataSourceManager";

import { v4 as uuidv4 } from "uuid";

import { styled } from "@mui/material/styles";
import { Box, Typography, Button, CircularProgress } from "@mui/material";

import CollapseCard from "components/Collapse";
import TwoColumnContainer from "components/Container/TwoColumnContainer";
import { SolidButton } from "components/buttons/SolidButton";
import { formatTimestamp, formatDuration } from "utils/format";

import btn_topology_expand from "assets/images/icons/btn_topology_expand.svg";
import btn_topology_expand_hover from "assets/images/icons/btn_topology_expand_hover.svg";

const StyledTypography = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.customFontSize[2],
  color: theme.palette.primary.black,
  fontWeight: theme.typography.customFontWeight[1],
}));

const StyledIconButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.primary.transparent,
  width: "16px",
  height: "16px",
  minWidth: "16px",
  padding: "0",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: theme.palette.primary.transparent,
  },
}));

const UnityProxyPanel = ({ auth, infoData, capabilityData }) => {
  const [hover, setHover] = useState({ expand: false });
  const supportPoePower = !!capabilityData.poePorts && capabilityData.poePorts.length > 0;

  function handleHover(key, isHover) {
    setHover((prev) => ({ ...prev, [key]: isHover }));
  }

  const fields = [
    { key: "deviceName", label: "Device Name", hidden: false },
    { key: "id", label: "Unity Proxy ID", hidden: false },
    { key: "instanceId", label: "Device ID", hidden: false },
    { key: "firmwareVersion", label: "Software Ver.", hidden: false },
    { key: "deviceTag", label: "Device Tag", withIcon: true, hidden: true },
    { key: "cpuStats", label: "CPU Utilization", hidden: false },
    { key: "memoryStats", label: "Memory Utilization", hidden: false },
    { key: "poePowerUsage", label: "PoE Power Usage", hidden: !supportPoePower },
    { key: "uptime", label: "Uptime", hidden: false },
    { key: "address", label: "Address", hidden: false },
  ];

  return (
    <CollapseCard title="Unity Proxy" >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, width: 560 }}>
        {fields.map(({ key, label, withIcon, hidden }) => {
          if (hidden) return null;

          if (key === "uptime" && infoData.linkStatus === "offline") {
            key = "lastSeen";
            label = "Last Seen";
          }

          let displayValue;

          switch (key) {
            case "poePowerUsage": {
              const { poeConsumption, poeBudget } = infoData;
              displayValue =
                poeConsumption != null && poeBudget != null
                  ? `${poeConsumption}/${poeBudget}W`
                  : "-";
              break;
            }
            case "cpuStats":
            case "memoryStats":
              displayValue = infoData[key] != null ? `${infoData[key]}%` : "-";
              break;
            case "lastSeen":
              displayValue =
                infoData.linkStatus === "online"
                  ? "online"
                  : infoData.lastOfflineTime
                    ? formatTimestamp(infoData.lastOfflineTime)
                    : "-";
              break;
            case "uptime": {
              const status = infoData.linkStatus;
              const uptime = infoData.uptime;
              const lastSeen = infoData.lastOfflineTime;

              if (status === "offline") {
                displayValue =
                  lastSeen !== null ? formatTimestamp(lastSeen) : "-";
              } else if (status === "online") {
                displayValue =
                  uptime !== null
                    ? formatDuration(
                      Math.max(0, Math.floor(Date.now() / 1000) - uptime)
                    ) // 算法參考#1094
                    : "-";
              } else {
                displayValue = "-";
              }

              break;
            }
            default:
              displayValue = infoData[key] != null ? infoData[key] : "-";
          }

          return (
            <TwoColumnContainer key={key} label={label}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 2,
                  alignItems: "center",
                }}
              >
                <StyledTypography>{displayValue}</StyledTypography>

                {withIcon && (
                  <StyledIconButton
                    onMouseEnter={() => handleHover("expand", true)}
                    onMouseLeave={() => handleHover("expand", false)}
                  >
                    <img
                      src={
                        hover.expand
                          ? btn_topology_expand_hover
                          : btn_topology_expand
                      }
                      alt="btn_topology_expand"
                    />
                  </StyledIconButton>
                )}
              </Box>
            </TwoColumnContainer>
          );
        })}

        <TwoColumnContainer label="Joined On">
          <StyledTypography>
            {infoData?.joinedOn ? formatTimestamp(infoData.joinedOn) : "-"}
          </StyledTypography>
        </TwoColumnContainer>
      </Box>
    </CollapseCard>
  );
};

export default UnityProxyPanel;
